import { getScoreBarColor, getScoreColor } from '../../lib/utils'
import { useLocale } from '../../i18n/LocaleProvider'

export function ScoreBar({ score, showLabel = false, size = 'md' }) {
  const { t } = useLocale()
  const heights = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
  }

  return (
    <div className="flex items-center gap-2">
      <div className={`flex-1 bg-gray-100 rounded-full ${heights[size]} overflow-hidden`}>
        <div
          className={`${heights[size]} rounded-full transition-all duration-500 ${getScoreBarColor(score)}`}
          style={{ width: `${Math.min(100, score)}%` }}
        />
      </div>
      <span className={`text-sm font-bold w-8 text-right tabular-nums ${getScoreColor(score)}`}>
        {score}
      </span>
      {showLabel && (
        <span className={`text-xs font-medium ${getScoreColor(score)}`}>
          {getLocalizedScoreLabel(score, t)}
        </span>
      )}
    </div>
  )
}

export function ScoreCircle({ score, size = 'md' }) {
  const sizes = {
    sm: { outer: 'w-10 h-10', text: 'text-sm', stroke: 28 },
    md: { outer: 'w-14 h-14', text: 'text-base', stroke: 36 },
    lg: { outer: 'w-20 h-20', text: 'text-xl', stroke: 52 },
    xl: { outer: 'w-24 h-24', text: 'text-2xl', stroke: 64 },
  }

  const { outer, text, stroke } = sizes[size]
  const radius = stroke / 2 - 4
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference

  const strokeColors = {
    excellent: '#22c55e',
    good: '#3b82f6',
    partial: '#eab308',
    poor: '#ef4444',
  }

  const getStrokeColor = (value) => {
    if (value >= 85) return strokeColors.excellent
    if (value >= 70) return strokeColors.good
    if (value >= 50) return strokeColors.partial
    return strokeColors.poor
  }

  return (
    <div className={`relative ${outer} flex items-center justify-center`}>
      <svg className="absolute inset-0 -rotate-90" viewBox={`0 0 ${stroke} ${stroke}`}>
        <circle
          cx={stroke / 2}
          cy={stroke / 2}
          r={radius}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth="3"
        />
        <circle
          cx={stroke / 2}
          cy={stroke / 2}
          r={radius}
          fill="none"
          stroke={getStrokeColor(score)}
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700"
        />
      </svg>
      <span className={`${text} font-bold ${getScoreColor(score)} tabular-nums`}>
        {score}
      </span>
    </div>
  )
}

export function ScoreBreakdown({ candidate, showDebug = false }) {
  const { t } = useLocale()
  const detailed = candidate.scoreBreakdown || candidate.score_breakdown
  const aiEvaluation = detailed?.aiEvaluation || detailed?.ai_evaluation || candidate.aiEvaluation || candidate.ai_evaluation
  const isV2Breakdown = Boolean(detailed?.skill_match || detailed?.rule_score)
  const mustHaveDetails = detailed?.must_have || detailed?.mustHave
  const showLegacyMustHaveDetails = Boolean(mustHaveDetails && !aiEvaluation)
  const breakdown = detailed
    ? aiEvaluation
      ? [
          ['coreDomainFit', { score: aiEvaluation.coreDomainFit || 0, max: 100, explanation: t('authenticated.score.descriptions.coreDomainFit', { score: aiEvaluation.coreDomainFit || 0 }) }],
          ['mustHaveMatch', { score: aiEvaluation.mustHaveMatch || 0, max: 100, explanation: t('authenticated.score.descriptions.mustHaveMatch', { score: aiEvaluation.mustHaveMatch || 0 }) }],
          ['seniorityFit', { score: aiEvaluation.seniorityFit || 0, max: 100, explanation: t('authenticated.score.descriptions.seniorityFit', { score: aiEvaluation.seniorityFit || 0 }) }],
          ['responsibilityFit', { score: aiEvaluation.responsibilityFit || 0, max: 100, explanation: t('authenticated.score.descriptions.responsibilityFit', { score: aiEvaluation.responsibilityFit || 0 }) }],
          ['toolsFit', { score: aiEvaluation.toolsFit || 0, max: 100, explanation: t('authenticated.score.descriptions.toolsFit', { score: aiEvaluation.toolsFit || 0 }) }],
          ['redFlags', { score: Math.max(0, 100 - ((aiEvaluation.redFlags || []).length * 20)), max: 100, explanation: (aiEvaluation.redFlags || []).length ? `${t('authenticated.score.redFlags')} ${(aiEvaluation.redFlags || []).slice(0, 4).join(', ')}.` : t('authenticated.score.descriptions.noMajorRedFlags') }],
          ...(showDebug
            ? [['ruleFallback', detailed.fallbackRuleScore || detailed.fallback_rule_score || detailed.rule_fallback || detailed.ruleFallback]]
            : []),
          ...(showDebug && detailed.guardrails ? [['guardrails', detailed.guardrails]] : []),
        ]
          .filter(([, item]) => Boolean(item))
          .map(([labelKey, item]) => ({
            label: t(`authenticated.score.labels.${labelKey}`),
            score: item.score || 0,
            max: item.max ?? item.maxScore ?? item.max_score ?? 0,
            description: item.explanation || '',
            color: getDetailedColor(labelKey),
            textColor: getDetailedTextColor(labelKey),
            bgColor: getDetailedBgColor(labelKey),
          }))
      : isV2Breakdown
      ? [
          ['skillMatch', detailed.skill_match],
          ['mustHave', detailed.must_have],
          ['experience', detailed.experience],
          ['ruleScore', detailed.rule_score],
          ['calibration', detailed.calibration],
          ['semanticScore', detailed.semantic_score],
        ]
          .filter(([, item]) => Boolean(item))
          .map(([labelKey, item]) => ({
            label: t(`authenticated.score.labels.${labelKey}`),
            score: item.score || 0,
            max: item.maxScore || item.max_score || 0,
            description: item.explanation || '',
            color: getDetailedColor(labelKey),
            textColor: getDetailedTextColor(labelKey),
            bgColor: getDetailedBgColor(labelKey),
          }))
      : [
          ['coreDomain', detailed.coreDomain],
          ['mustHave', detailed.mustHave],
          ['roleTitle', detailed.roleTitle],
          ['responsibility', detailed.responsibility],
          ['seniority', detailed.seniority],
          ['toolPlatform', detailed.toolPlatform],
          ['geographyRegulatory', detailed.geoRegulatory],
          ['credential', detailed.credential],
          ['penalty', detailed.penalty],
        ]
          .filter(([, item]) => Boolean(item))
          .map(([labelKey, item]) => ({
            label: t(`authenticated.score.labels.${labelKey}`),
            score: item.score || 0,
            max: item.maxScore || 0,
            description: item.explanation || '',
            color: getDetailedColor(labelKey),
            textColor: getDetailedTextColor(labelKey),
            bgColor: getDetailedBgColor(labelKey),
          }))
    : [
        {
          label: t('authenticated.score.labels.skillMatch'),
          score: candidate.skillMatchScore || 0,
          max: 40,
          color: 'bg-blue-500',
          textColor: 'text-blue-700',
          bgColor: 'bg-blue-50',
          description: getSkillDescription(candidate.skillMatchScore || 0, t),
        },
        {
          label: t('authenticated.score.labels.experience'),
          score: candidate.experienceScore || 0,
          max: 30,
          color: 'bg-green-500',
          textColor: 'text-green-700',
          bgColor: 'bg-green-50',
          description: getExperienceDescription(
            candidate.experienceScore || 0,
            candidate.experienceYears || 0,
            t
          ),
        },
        {
          label: t('authenticated.score.labels.education'),
          score: candidate.educationScore || 0,
          max: 15,
          color: 'bg-purple-500',
          textColor: 'text-purple-700',
          bgColor: 'bg-purple-50',
          description: getEducationDescription(candidate.educationScore || 0, t),
        },
        {
          label: t('authenticated.score.labels.keywordMatch'),
          score: candidate.keywordScore || 0,
          max: 15,
          color: 'bg-orange-500',
          textColor: 'text-orange-700',
          bgColor: 'bg-orange-50',
          description: getKeywordDescription(candidate.keywordScore || 0, t),
        },
      ]

  return (
    <div className="space-y-4">
      {breakdown.map((item, index) => (
        <div key={`${item.label}-${index}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-medium text-gray-700">{item.label}</span>
            <span className={`text-sm font-bold ${item.textColor}`}>
              {item.score}/{item.max}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 mb-1.5">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${item.color}`}
              style={{ width: `${item.max > 0 ? (item.score / item.max) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-gray-500">{item.description}</p>
        </div>
      ))}

      <div
        className={`rounded-xl p-4 border mt-4 ${
          candidate.matchScore >= 80
            ? 'bg-green-50 border-green-200'
            : candidate.matchScore >= 60
            ? 'bg-blue-50 border-blue-200'
            : 'bg-gray-50 border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-800">{t('authenticated.score.overall')}</span>
          <span className={`text-xl font-bold ${getScoreColor(candidate.matchScore)}`}>
            {candidate.matchScore}/100
          </span>
        </div>
        <p className="text-xs text-gray-600 mt-1">
          {getOverallDescription(candidate.matchScore, t)}
        </p>
      </div>

      {aiEvaluation && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-gray-800">{t('authenticated.score.aiEvaluation')}</span>
            <span className="text-xs font-medium text-gray-500 tabular-nums">
              {aiEvaluation.recommendation || 'partial'}
            </span>
          </div>
          <p className="mt-2 text-xs text-gray-600">
            {aiEvaluation.explanation || t('authenticated.score.noAiExplanation')}
          </p>
          <div className="mt-3 grid gap-2 text-xs text-gray-700">
            <div>
              <span className="font-medium text-gray-800">{t('authenticated.score.redFlags')}</span>{' '}
              {(aiEvaluation.redFlags || []).slice(0, 6).join(', ') || t('authenticated.score.none')}
            </div>
          </div>
        </div>
      )}

      {showDebug && showLegacyMustHaveDetails && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-gray-800">{t('authenticated.score.mustHaveDetail')}</span>
            <span className="text-xs font-medium text-gray-500 tabular-nums">
              {t('authenticated.score.ratio', { value: (mustHaveDetails.must_have_ratio ?? mustHaveDetails.mustHaveRatio ?? 0).toFixed(2) })}
            </span>
          </div>
          <p className="mt-2 text-xs text-gray-600">
            {mustHaveDetails.explanation || t('authenticated.score.noMustHaveExplanation')}
          </p>
          <div className="mt-3 grid gap-2 text-xs text-gray-700">
            <div>
              <span className="font-medium text-gray-800">{t('authenticated.score.hardTerms')}</span>{' '}
              {(mustHaveDetails.extracted_terms || mustHaveDetails.extractedTerms || [])
                .slice(0, 8)
                .join(', ') || t('authenticated.score.none')}
            </div>
            <div>
              <span className="font-medium text-gray-800">{t('authenticated.score.matched')}</span>{' '}
              {(mustHaveDetails.matched_terms || mustHaveDetails.matchedTerms || [])
                .slice(0, 8)
                .join(', ') || t('authenticated.score.none')}
            </div>
            <div>
              <span className="font-medium text-gray-800">{t('authenticated.score.supporting')}</span>{' '}
              {(mustHaveDetails.supporting_terms || mustHaveDetails.supportingTerms || [])
                .slice(0, 8)
                .join(', ') || t('authenticated.score.none')}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function getSkillDescription(score, t) {
  if (score >= 35) return t('authenticated.score.descriptions.excellentSkill')
  if (score >= 25) return t('authenticated.score.descriptions.goodSkill')
  if (score >= 15) return t('authenticated.score.descriptions.partialSkill')
  return t('authenticated.score.descriptions.lowSkill')
}

function getExperienceDescription(score, years, t) {
  if (score >= 28) return t('authenticated.score.descriptions.highExperience', { years })
  if (score >= 23) return t('authenticated.score.descriptions.strongExperience', { years })
  if (score >= 16) return t('authenticated.score.descriptions.developingExperience', { years })
  if (score >= 8) return t('authenticated.score.descriptions.earlyExperience', { years })
  return t('authenticated.score.descriptions.limitedExperience')
}

function getEducationDescription(score, t) {
  if (score >= 15) return t('authenticated.score.descriptions.degreeCerts')
  if (score >= 10) return t('authenticated.score.descriptions.degreeOnly')
  if (score >= 5) return t('authenticated.score.descriptions.certsOnly')
  return t('authenticated.score.descriptions.noEducation')
}

function getKeywordDescription(score, t) {
  if (score >= 12) return t('authenticated.score.descriptions.closeKeyword')
  if (score >= 8) return t('authenticated.score.descriptions.goodKeyword')
  if (score >= 4) return t('authenticated.score.descriptions.someKeyword')
  return t('authenticated.score.descriptions.lowKeyword')
}

function getOverallDescription(score, t) {
  if (score >= 85) return t('authenticated.score.descriptions.overallStrong')
  if (score >= 65) return t('authenticated.score.descriptions.overallGood')
  if (score >= 50) return t('authenticated.score.descriptions.overallModerate')
  return t('authenticated.score.descriptions.overallWeak')
}

function getLocalizedScoreLabel(score, t) {
  if (score >= 85) return t('authenticated.badges.score.excellent')
  if (score >= 70) return t('authenticated.badges.score.good')
  if (score >= 50) return t('authenticated.badges.score.partial')
  return t('authenticated.badges.score.poor')
}

function getDetailedColor(label) {
  const normalized = String(label || '').toLowerCase()
  if (normalized.includes('coredomain')) return 'bg-blue-500'
  if (normalized.includes('musthave')) return 'bg-green-500'
  if (normalized.includes('seniority')) return 'bg-emerald-500'
  if (normalized.includes('responsibility')) return 'bg-cyan-500'
  if (normalized.includes('tools')) return 'bg-orange-500'
  if (normalized.includes('redflags')) return 'bg-red-500'
  if (normalized.includes('rulefallback')) return 'bg-indigo-500'
  if (normalized.includes('skillmatch')) return 'bg-blue-500'
  if (normalized.includes('rulescore')) return 'bg-indigo-500'
  if (normalized.includes('semanticscore')) return 'bg-cyan-500'
  if (normalized.includes('roletitle')) return 'bg-indigo-500'
  if (normalized.includes('toolplatform')) return 'bg-orange-500'
  if (normalized.includes('geographyregulatory')) return 'bg-fuchsia-500'
  if (normalized.includes('core domain')) return 'bg-blue-500'
  if (normalized.includes('must-have')) return 'bg-green-500'
  if (normalized.includes('seniority fit')) return 'bg-emerald-500'
  if (normalized.includes('responsibility fit')) return 'bg-cyan-500'
  if (normalized.includes('tools fit')) return 'bg-orange-500'
  if (normalized.includes('red flags')) return 'bg-red-500'
  if (normalized.includes('fallback')) return 'bg-indigo-500'
  if (normalized.includes('skill')) return 'bg-blue-500'
  if (normalized.includes('experience')) return 'bg-green-500'
  if (normalized.includes('rule')) return 'bg-indigo-500'
  if (normalized.includes('calibration')) return 'bg-emerald-500'
  if (normalized.includes('semantic')) return 'bg-cyan-500'
  if (normalized.includes('core')) return 'bg-blue-500'
  if (normalized.includes('must')) return 'bg-green-500'
  if (normalized.includes('role')) return 'bg-indigo-500'
  if (normalized.includes('respons')) return 'bg-cyan-500'
  if (normalized.includes('senior')) return 'bg-emerald-500'
  if (normalized.includes('tool')) return 'bg-orange-500'
  if (normalized.includes('geo')) return 'bg-fuchsia-500'
  if (normalized.includes('cred')) return 'bg-purple-500'
  if (normalized.includes('penalty')) return 'bg-red-500'
  return 'bg-gray-500'
}

function getDetailedTextColor(label) {
  const normalized = String(label || '').toLowerCase()
  if (normalized.includes('coredomain')) return 'text-blue-700'
  if (normalized.includes('musthave')) return 'text-green-700'
  if (normalized.includes('seniority')) return 'text-emerald-700'
  if (normalized.includes('responsibility')) return 'text-cyan-700'
  if (normalized.includes('tools')) return 'text-orange-700'
  if (normalized.includes('redflags')) return 'text-red-700'
  if (normalized.includes('rulefallback')) return 'text-indigo-700'
  if (normalized.includes('skillmatch')) return 'text-blue-700'
  if (normalized.includes('rulescore')) return 'text-indigo-700'
  if (normalized.includes('semanticscore')) return 'text-cyan-700'
  if (normalized.includes('roletitle')) return 'text-indigo-700'
  if (normalized.includes('toolplatform')) return 'text-orange-700'
  if (normalized.includes('geographyregulatory')) return 'text-fuchsia-700'
  if (normalized.includes('core domain')) return 'text-blue-700'
  if (normalized.includes('must-have')) return 'text-green-700'
  if (normalized.includes('seniority fit')) return 'text-emerald-700'
  if (normalized.includes('responsibility fit')) return 'text-cyan-700'
  if (normalized.includes('tools fit')) return 'text-orange-700'
  if (normalized.includes('red flags')) return 'text-red-700'
  if (normalized.includes('fallback')) return 'text-indigo-700'
  if (normalized.includes('skill')) return 'text-blue-700'
  if (normalized.includes('experience')) return 'text-green-700'
  if (normalized.includes('rule')) return 'text-indigo-700'
  if (normalized.includes('calibration')) return 'text-emerald-700'
  if (normalized.includes('semantic')) return 'text-cyan-700'
  if (normalized.includes('core')) return 'text-blue-700'
  if (normalized.includes('must')) return 'text-green-700'
  if (normalized.includes('role')) return 'text-indigo-700'
  if (normalized.includes('respons')) return 'text-cyan-700'
  if (normalized.includes('senior')) return 'text-emerald-700'
  if (normalized.includes('tool')) return 'text-orange-700'
  if (normalized.includes('geo')) return 'text-fuchsia-700'
  if (normalized.includes('cred')) return 'text-purple-700'
  if (normalized.includes('penalty')) return 'text-red-700'
  return 'text-gray-700'
}

function getDetailedBgColor(label) {
  const normalized = String(label || '').toLowerCase()
  if (normalized.includes('coredomain')) return 'bg-blue-50'
  if (normalized.includes('musthave')) return 'bg-green-50'
  if (normalized.includes('seniority')) return 'bg-emerald-50'
  if (normalized.includes('responsibility')) return 'bg-cyan-50'
  if (normalized.includes('tools')) return 'bg-orange-50'
  if (normalized.includes('redflags')) return 'bg-red-50'
  if (normalized.includes('rulefallback')) return 'bg-indigo-50'
  if (normalized.includes('skillmatch')) return 'bg-blue-50'
  if (normalized.includes('rulescore')) return 'bg-indigo-50'
  if (normalized.includes('semanticscore')) return 'bg-cyan-50'
  if (normalized.includes('roletitle')) return 'bg-indigo-50'
  if (normalized.includes('toolplatform')) return 'bg-orange-50'
  if (normalized.includes('geographyregulatory')) return 'bg-fuchsia-50'
  if (normalized.includes('core domain')) return 'bg-blue-50'
  if (normalized.includes('must-have')) return 'bg-green-50'
  if (normalized.includes('seniority fit')) return 'bg-emerald-50'
  if (normalized.includes('responsibility fit')) return 'bg-cyan-50'
  if (normalized.includes('tools fit')) return 'bg-orange-50'
  if (normalized.includes('red flags')) return 'bg-red-50'
  if (normalized.includes('fallback')) return 'bg-indigo-50'
  if (normalized.includes('skill')) return 'bg-blue-50'
  if (normalized.includes('experience')) return 'bg-green-50'
  if (normalized.includes('rule')) return 'bg-indigo-50'
  if (normalized.includes('calibration')) return 'bg-emerald-50'
  if (normalized.includes('semantic')) return 'bg-cyan-50'
  if (normalized.includes('core')) return 'bg-blue-50'
  if (normalized.includes('must')) return 'bg-green-50'
  if (normalized.includes('role')) return 'bg-indigo-50'
  if (normalized.includes('respons')) return 'bg-cyan-50'
  if (normalized.includes('senior')) return 'bg-emerald-50'
  if (normalized.includes('tool')) return 'bg-orange-50'
  if (normalized.includes('geo')) return 'bg-fuchsia-50'
  if (normalized.includes('cred')) return 'bg-purple-50'
  if (normalized.includes('penalty')) return 'bg-red-50'
  return 'bg-gray-50'
}
